import { z } from 'zod';
import { fiscalType, isoDate, monthKey } from './primitives.js';

/**
 * Lancamento do extrato. Espelha a tabela `transactions`.
 *
 * `posted_date` e a data em que o banco liquidou; `competencia` e o mes ao qual
 * o lancamento pertence no DRE. Elas divergem de proposito — uma despesa paga
 * em 02/02 pode competir a 01/2026 — e e por isso que a competencia e coluna
 * propria, editavel, e nao derivada da data.
 */
export const transaction = z.object({
  id: z.number().int().positive(),
  /** Banco de origem. A DRE soma varios, e e ele que separa a distribuicao. */
  bank_id: z.number().int().positive(),
  /** Identificador emitido pelo banco. Nulo em lancamento manual. */
  fitid: z.string().nullable(),
  /**
   * ATENCAO: a API entrega a data ja formatada em `dd/mm/aaaa`, nao em ISO.
   * A query usa `DATE_FORMAT(posted_date, '%d/%m/%Y')` e o front legado
   * dependia disso. Mantido na migracao para nao alterar a saida das rotas;
   * use `parsePostedDate()` quando precisar de um `Date`.
   */
  posted_date: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/, 'Data deve vir como dd/mm/aaaa.'),
  /** Negativo = saida, positivo = entrada. DECIMAL(12,2) lido como number. */
  amount: z.number(),
  description: z.string().nullable(),
  trn_type: z.string().nullable(),
  category: z.string().nullable(),
  complemento: z.string().nullable(),
  fiscal_type: z.string(),
  receipt_path: z.string().nullable(),
  competencia: z.string().nullable(),
});
export type Transaction = z.infer<typeof transaction>;

/** Converte o `dd/mm/aaaa` que a API devolve para `Date`. */
export function parsePostedDate(value: string): Date | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
}

/**
 * Direcao do lancamento. A API legada aceitava varios sinonimos no mesmo
 * parametro (`INCOME`, `ENTRADA`, `SAIDAS`...); o contrato normalizado expoe
 * so os tres valores canonicos, e o adaptador da API continua tolerando os
 * sinonimos antigos para nao quebrar link compartilhado ja em circulacao.
 */
export const transactionFlow = z.enum(['all', 'CREDIT', 'DEBIT']);
export type TransactionFlow = z.infer<typeof transactionFlow>;

/** Filtros aceitos por `GET /api/transactions`. */
export const transactionQuery = z.object({
  /** Competencia ou mes de caixa, conforme `filterBy`. */
  period: monthKey.optional(),
  /** `date` filtra por `posted_date`; `competencia`, pela coluna homonima. */
  filterBy: z.enum(['date', 'competencia']).default('date'),
  status: z.enum(['all', 'classified', 'unclassified']).default('all'),
  /** Nome exato da categoria, ou `all`. */
  category: z.string().max(128).default('all'),
  flow: z.string().optional(),
  /** Casa com descricao, complemento e tambem com o valor absoluto. */
  search: z.string().max(200).optional(),
  /** Intervalo explicito. Quando presente, tem precedencia sobre `period`. */
  start_date: isoDate.optional(),
  end_date: isoDate.optional(),
});
export type TransactionQuery = z.infer<typeof transactionQuery>;

/** Campos que a tela de classificacao pode alterar (PATCH parcial). */
export const transactionPatch = z
  .object({
    category: z.string().max(128).nullable(),
    complemento: z.string().max(255).nullable(),
    fiscal_type: fiscalType,
    competencia: monthKey,
  })
  .partial()
  .refine((body) => Object.keys(body).length > 0, {
    message: 'Envie ao menos um campo para alterar.',
  });
export type TransactionPatch = z.infer<typeof transactionPatch>;

/**
 * Lancamento criado a mao, para o que nao veio no OFX (dinheiro, pix de outra
 * conta, ajuste). Entra sem `fitid` e fora da deduplicacao do extrato.
 */
export const manualTransaction = z.object({
  posted_date: isoDate,
  amount: z.number(),
  description: z.string().trim().min(1, 'Descricao e obrigatoria.').max(500),
  category: z.string().max(128).nullish(),
  complemento: z.string().max(255).nullish(),
  fiscal_type: fiscalType.optional(),
  competencia: monthKey.optional(),
});
export type ManualTransaction = z.infer<typeof manualTransaction>;
